/**
 * Custom implementation of validateCredentials API without retries
 * This bypasses the third-party library entirely
 */

/**
 * Create structured error object to match parseV4Error expectations
 * @param {number} status - HTTP status code
 * @param {string} errorData - Raw error response text
 * @param {string} errorCode - Error code (optional)
 * @param {string} errorMessage - Error message (optional)
 * @returns {object} Structured error object
 */
const createStructuredError = (status, errorData, errorCode = null, errorMessage = null) => {
    let parsedErrorData;
    try {
        parsedErrorData = JSON.parse(errorData);
    } catch {
        parsedErrorData = { message: errorData };
    }

    // Extract error details from the actual API response format:
    // parsedErrorData.data.error = [{ code: "IAM-23104", message: "...", ... }]
    const errorArray = parsedErrorData?.data?.error || [];

    return {
        status,
        data: {
            getData: () => ({
                getError: () =>
                    errorArray.length > 0
                        ? errorArray.map(errorItem => ({
                              getCode: () => errorCode || errorItem?.code || `HTTP_${status}`,
                              getMessage: () => errorMessage || errorItem?.message || errorData,
                              // Preserve additional error details
                              getErrorGroup: () => errorItem?.errorGroup,
                              getSeverity: () => errorItem?.severity,
                              getArgumentsMap: () => errorItem?.argumentsMap,
                          }))
                        : [
                              {
                                  getCode: () => errorCode || `HTTP_${status}`,
                                  getMessage: () => errorMessage || errorData,
                              },
                          ],
            }),
        },
        response: {
            text: errorData,
            status,
        },
        // Preserve the original parsed data for debugging
        originalData: parsedErrorData,
    };
};

/**
 * Direct implementation of validateCredentials without any retry mechanism
 * @param {string} extId - Login request identifier
 * @param {object} credentials - Object containing username and password
 * @returns {Promise} Promise that resolves/rejects immediately on 401 (no retries)
 */
export const validateCredentialsNoRetry = async (extId, credentials) => {
    const url = `/api/iam/v4.1.b2/authn/login-requests/${extId}/$actions/validate-credentials`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                // Add any other headers your API requires (auth, request-id, etc.)
            },
            credentials: 'include', // Include cookies if needed
            body: JSON.stringify(credentials),
        });

        if (!response.ok) {
            const errorData = await response.text();
            throw createStructuredError(response.status, errorData);
        }

        const responseData = await response.text();
        return {
            response: {
                text: responseData,
                status: response.status,
            },
        };
    } catch (error) {
        // Handle fetch errors (network issues, CORS, etc.) or re-throw our structured errors
        if (error.data?.getData) {
            // This is our structured error, re-throw as-is
            throw error;
        }

        // This is a fetch error (network, CORS, etc.), structure it using shared function
        throw createStructuredError(
            error.status || 0,
            error.message || 'Network error occurred',
            'NETWORK_ERROR',
            error.message || 'Network error occurred'
        );
    }
};

/**
 * Create a custom API object with our no-retry validateCredentials
 * @returns {object} API object with validateCredentials method
 */
export const createNoRetryLoginRequestsApi = () => {
    return {
        validateCredentials: validateCredentialsNoRetry,
    };
};
